import { zodResolver } from '@hookform/resolvers/zod';
import { addDays, subMonths } from 'date-fns';
import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Pressable, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { z } from 'zod';

import { Card, ControlledInput, Text } from '@/ui';
import { DatetimePopoverField } from '@/ui/datetime-popover-field';
import { Icon } from '@/ui/icon';
import { TrashFill } from '@/ui/icons/trash-fill';

export const addTollFormSchema = z.object({
  entryPlazaName: z.string().min(2, 'Minimum 2 characters'),
  exitPlazaName: z.string().optional(),
  entryDate: z.date(),
  exitDate: z.date().optional(),
  amount: z
    .string()
    .min(1, 'Amount is required')
    .regex(/^\d+(\.\d{1,2})?$/, 'Must be a valid number'),
});

export type AddTollFormType = z.infer<typeof addTollFormSchema>;

export default function TollForm({
  index,
  onChange,
  onValidChange,
  onRemove,
  onCalendarOpenChange,
  pickupTz,
}: {
  index: number;
  onChange: (index: number, values: AddTollFormType) => void;
  onValidChange: (index: number, isValid: boolean) => void;
  onRemove: (index: number) => void;
  onCalendarOpenChange: (isOpen: boolean) => void;
  pickupTz: string;
}) {
  const { styles, theme } = useStyles(stylesheet);
  const {
    control,
    trigger,
    setValue,
    formState: { isValid },
    getValues,
    reset,
    watch,
  } = useForm<AddTollFormType>({
    resolver: zodResolver(addTollFormSchema),
    mode: 'onChange',
  });

  const [entryDate, exitDate] = useWatch({
    control,
    name: ['entryDate', 'exitDate'],
  });

  useEffect(() => {
    onValidChange(index, isValid);
  }, [isValid]);

  // Notify parent when values change
  useEffect(() => {
    const sub = watch(() => {
      onChange(index, getValues());
    });
    return () => sub.unsubscribe();
  }, [watch]);

  const handleEntryChange = (startDt: Date) => {
    if (exitDate && startDt >= exitDate) {
      setValue('exitDate', addDays(startDt, 3), { shouldValidate: true });
    }

    setValue('entryDate', startDt, { shouldValidate: true });
  };

  return (
    <Card style={{ gap: 16, padding: 16 }}>
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Text type="bodyRegular" style={{ color: theme.colors.text.secondary }}>
          Toll {index + 1}
        </Text>

        {index > 0 ? (
          <Pressable
            onPress={() => {
              onRemove(index);
            }}
          >
            <Icon
              type="dressed"
              icon={<TrashFill />}
              width={12}
              height={12}
              fillColor={theme.colors.icon.errorColor}
            />
          </Pressable>
        ) : null}
      </View>

      <ControlledInput
        control={control}
        name="entryPlazaName"
        label="Entry Plaza Name"
        onBlur={() => {
          trigger('entryPlazaName');
        }}
        onChangeText={(text) => {
          trigger('entryPlazaName');
          setValue('entryPlazaName', text);
        }}
      />

      <ControlledInput
        control={control}
        name="exitPlazaName"
        label="Exit Plaza Name"
        onBlur={() => {
          trigger('exitPlazaName');
        }}
        onChangeText={(text) => {
          trigger('exitPlazaName');
          setValue('exitPlazaName', text);
        }}
      />

      <View style={{ flexDirection: 'row', zIndex: 2, gap: 16 }}>
        <DatetimePopoverField
          id="entry-date-calendar"
          label="Entry Date"
          value={entryDate}
          minDate={subMonths(new Date(), 6)}
          timeZone={pickupTz}
          onChange={handleEntryChange}
          onOpenChange={onCalendarOpenChange}
          containerStyle={{ flex: 1 }}
        />

        <DatetimePopoverField
          id="exit-date-calendar"
          label="Exit Date"
          value={exitDate}
          minDate={entryDate ? entryDate : subMonths(new Date(), 2)}
          timeZone={pickupTz}
          onChange={(d) => {
            setValue('exitDate', d, { shouldValidate: true });
          }}
          onOpenChange={onCalendarOpenChange}
          containerStyle={{ flex: 1 }}
        />
      </View>

      <ControlledInput
        control={control}
        name="amount"
        label="Amount"
        keyboardType="decimal-pad"
        inputMode="decimal"
        onBlur={() => {
          trigger('amount');
        }}
        onChangeText={(text) => {
          setValue('amount', text.replace(/[^0-9.]/g, ''));
          trigger('amount');
        }}
      />
    </Card>
  );
}

const stylesheet = createStyleSheet((theme) => ({}));
